import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { Crown } from 'lucide-react';
import { useAuth } from '@hooks/useAuth';

import { cn } from '@lib/utils';

// Loyalty tier type
type LoyaltyTier = 'bronze' | 'silver' | 'gold' | 'platinum' | null;

// const loyaltyColors = {
//   bronze: 'text-amber-600 bg-amber-100',
//   silver: 'text-gray-600 bg-gray-100',
//   gold: 'text-yellow-500 bg-yellow-100',
//   platinum: 'text-purple-600 bg-purple-100',
// };

// Helper function to extract size from className and calculate loyalty icon dimensions
const getLoyaltyIconSizes = (className?: string) => {
  // Default sizes for size-8 (32px)
  let iconContainerSize = 'w-5 h-5'; // 20px
  let iconSize = 'w-5 h-5'; // 12px
  let positioning = '-top-1 -right-1';

  if (className) {
    // Check for various size classes
    if (className.includes('size-4') || className.includes('h-4') || className.includes('w-4')) {
      // 16px avatar
      iconContainerSize = 'w-3 h-3'; // 12px
      iconSize = 'w-3 h-3'; // 8px
      positioning = '-top-0.5 -right-0.5';
    } else if (
      className.includes('size-6') ||
      className.includes('h-6') ||
      className.includes('w-6')
    ) {
      // 24px avatar
      iconContainerSize = 'w-4 h-4'; // 16px
      iconSize = 'w-4 h-4'; // 10px
      positioning = '-top-0.5 -right-0.5';
    } else if (
      className.includes('size-8') ||
      className.includes('h-8') ||
      className.includes('w-8')
    ) {
      // 32px avatar (default)
      iconContainerSize = 'w-5 h-5'; // 20px
      iconSize = 'w-5 h-5'; // 12px
      positioning = '-top-1 -right-1';
    } else if (
      className.includes('size-10') ||
      className.includes('h-10') ||
      className.includes('w-10')
    ) {
      // 40px avatar
      iconContainerSize = 'w-6 h-6'; // 24px
      iconSize = 'w-6 h-6'; // 14px
      positioning = '-top-1 -right-1';
    } else if (
      className.includes('size-12') ||
      className.includes('h-12') ||
      className.includes('w-12')
    ) {
      // 48px avatar
      iconContainerSize = 'w-7 h-7'; // 28px
      iconSize = 'w-7 h-7'; // 16px
      positioning = '-top-1.5 -right-1.5';
    } else if (
      className.includes('size-16') ||
      className.includes('h-16') ||
      className.includes('w-16')
    ) {
      // 64px avatar
      iconContainerSize = 'w-9 h-9'; // 36px
      iconSize = 'w-9 h-9'; // 20px
      positioning = '-top-2 -right-2';
    } else if (
      className.includes('size-20') ||
      className.includes('h-20') ||
      className.includes('w-20')
    ) {
      // 80px avatar
      iconContainerSize = 'w-11 h-11'; // 44px
      iconSize = 'w-11 h-11'; // 24px
      positioning = '-top-2.5 -right-2.5';
    } else if (
      className.includes('size-24') ||
      className.includes('h-24') ||
      className.includes('w-24')
    ) {
      // 96px avatar
      iconContainerSize = 'w-12 h-12'; // 48px
      iconSize = 'w-12 h-12'; // 28px
      positioning = '-top-3 -right-3';
    }
  }

  return { iconContainerSize, iconSize, positioning };
};

interface AvatarProps extends React.ComponentProps<typeof AvatarPrimitive.Root> {
  loyaltyTier?: LoyaltyTier;
  showLoyaltyIcon?: boolean;
  enableCoinAnimation?: boolean;
  loyaltyDisplayMode?: 'flip' | 'corner';
  showProgressCircle?: boolean;
  currentPoints?: number;
  nextLevelPoints?: number;
  isMerchant?: boolean;
  lg?: boolean;
}

function Avatar({
  className,
  loyaltyTier,
  showLoyaltyIcon = false,
  enableCoinAnimation = false,
  loyaltyDisplayMode = 'corner',
  showProgressCircle = false,
  currentPoints = 0,
  nextLevelPoints = 100,
  isMerchant = false,
  lg = false,
  ...props
}: AvatarProps) {
  const { isAuthenticated } = useAuth();
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [showLoyaltyTemporary, setShowLoyaltyTemporary] = React.useState(false);

  React.useEffect(() => {
    // Only enable coin animation for authenticated users
    if (!enableCoinAnimation || !isAuthenticated) return;

    const interval = setInterval(() => {
      setIsAnimating(true);

      // After flip animation (1 second), show loyalty icon for 3 seconds
      setTimeout(() => {
        setIsAnimating(false);
        setShowLoyaltyTemporary(true);

        // Start fade out animation 300ms before hiding
        setTimeout(() => {
          // Hide loyalty icon after fade out animation completes
          setTimeout(() => {
            setShowLoyaltyTemporary(false);
            // Trigger flip back from crown to avatar
            setIsAnimating(true);
            setTimeout(() => {
              setIsAnimating(false);
            }, 1000); // Match coinFlip duration
          }, 300); // Fade out duration
        }, 2700); // Show for 2.7s then start fade out
      }, 1000); // Animation duration
    }, 15000); // Every 15 seconds

    return () => clearInterval(interval);
  }, [enableCoinAnimation, loyaltyDisplayMode, isAuthenticated]);

  // const LoyaltyIcon = loyaltyTier ? loyaltyIcons[loyaltyTier] : null;
  // Get tier color based on loyaltyTier
  const getTierColor = (tier: LoyaltyTier) => {
    switch (tier?.toLowerCase()) {
      case 'bronze':
        return 'text-[#D3A575]';
      case 'silver':
        return 'text-[#C6C1AF]';
      case 'gold':
        return 'text-[#FFC200]';
      case 'platinum':
        return 'text-[#9333ea]';
      default:
        return 'text-gray-400'; // default to gray color for null/unknown tiers
    }
  };

  const { iconSize, positioning } = getLoyaltyIconSizes(className);

  // Calculate progress percentage for the circle
  const progressPercentage =
    nextLevelPoints > 0 ? Math.min((currentPoints / nextLevelPoints) * 100, 100) : 0;

  return (
    <>
      {/* Add CSS keyframes to document head */}
      {(enableCoinAnimation || isMerchant) && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
            @keyframes coinFlip {
              0% { transform: rotateY(0deg); }
              50% { transform: rotateY(180deg); }
              100% { transform: rotateY(360deg); }
            }
            .coin-flip-animation {
              animation: coinFlip 1s ease-in-out;
            }
            @keyframes fadeIn {
              0% { opacity: 0; transform: scale(0.8); }
              100% { opacity: 1; transform: scale(1); }
            }
            @keyframes fadeOut {
              0% { opacity: 1; transform: scale(1); }
              100% { opacity: 0; transform: scale(0.8); }
            }
            .loyalty-fade-in {
              animation: fadeIn 0.3s ease-out forwards;
            }
            .loyalty-fade-out {
              animation: fadeOut 0.3s ease-in forwards;
            }
            /* Google-like merchant circle animation */
            @keyframes pulseRing {
              0% { transform: scale(0.9); opacity: 0.7; }
              50% { transform: scale(1.1); opacity: 1; }
              100% { transform: scale(0.9); opacity: 0.7; }
            }
            .merchant-circle-pulse {
              animation: pulseRing 2s infinite;
            }
          `,
          }}
        />
      )}

      <div className="relative inline-block">
        {/* Progress Circle */}
        {showProgressCircle && (
          <svg
            className="absolute inset-0 w-full h-full -rotate-90 pointer-events-none"
            style={{ zIndex: 10 }}
            viewBox="0 0 100 100"
          >
            {/* Background circle */}
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="rgba(255, 255, 255, 0.2)"
              strokeWidth="3"
            />
            {/* Progress circle */}
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="url(#progressGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="282.74"
              strokeDashoffset={282.74 - (progressPercentage / 100) * 282.74}
              style={{
                transition: 'stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            />
            {/* White gradient definition */}
            <defs>
              <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                <stop offset="50%" stopColor="#f8fafc" stopOpacity="1" />
                <stop offset="100%" stopColor="#e2e8f0" stopOpacity="1" />
              </linearGradient>
            </defs>
          </svg>
        )}

        <AvatarPrimitive.Root
          data-slot="avatar"
          className={cn(
            'relative flex size-8 shrink-0 overflow-hidden rounded-full',
            isAnimating &&
              enableCoinAnimation &&
              loyaltyDisplayMode === 'flip' &&
              isAuthenticated &&
              'coin-flip-animation',
            className,
          )}
          style={{ zIndex: 2 }}
          {...props}
        />

        {/* ChatGPT-like Plus Badge with "mer." text */}
        {isMerchant && (
          <div
            className={cn(
              'absolute -bottom-1 -right-1 flex items-center text-white justify-center rounded-full bg-amber-500 border border-white/40 shadow-md',
              lg && '-translate-x-1/2 -translate-y-1/2 px-2',
            )}
            style={{
              zIndex: 20,
              padding: lg ? '2px 6px' : '2px 4px',
              fontSize: '0.6rem',
              lineHeight: '1',
              minWidth: '36px',
              fontWeight: 'bold',
            }}
          >
            mer.
          </div>
        )}

        {/* Loyalty Icon - completely replaces avatar for 3 seconds after flip (flip mode) */}
        {isAuthenticated &&
          enableCoinAnimation &&
          loyaltyDisplayMode === 'flip' &&
          showLoyaltyTemporary &&
          loyaltyTier && (
            <div
              className={cn(
                'absolute inset-0 flex items-center justify-center bg-white rounded-full border-2 border-gray-200',
              )}
              style={{ zIndex: 4 }}
            >
              <Crown className={cn('w-3/4 h-3/4', getTierColor(loyaltyTier))} />
            </div>
          )}

        {/* Loyalty Icon - corner overlay after flip (corner mode) */}
        {isAuthenticated &&
          enableCoinAnimation &&
          loyaltyDisplayMode === 'corner' &&
          showLoyaltyTemporary &&
          loyaltyTier && (
            <div
              className={cn('absolute flex items-center justify-center', positioning)}
              style={{ zIndex: 10 }}
            >
              <Crown className={cn(iconSize, getTierColor(loyaltyTier))} />
            </div>
          )}

        {/* Static Loyalty Icon - show when showLoyaltyIcon is true or corner mode is enabled */}
        {isAuthenticated &&
          ((showLoyaltyIcon && !enableCoinAnimation) ||
            (loyaltyDisplayMode === 'corner' && enableCoinAnimation && !showLoyaltyTemporary)) &&
          loyaltyTier && (
            <div
              className={cn(
                'absolute flex items-center justify-center',
                positioning,
                isAnimating &&
                  enableCoinAnimation &&
                  loyaltyDisplayMode === 'corner' &&
                  'coin-flip-animation',
              )}
              style={{ zIndex: 10 }}
            >
              <Crown className={cn(iconSize, getTierColor(loyaltyTier))} />
            </div>
          )}
      </div>
    </>
  );
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full', className)}
      {...props}
    />
  );
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn('bg-muted flex size-full items-center justify-center rounded-full', className)}
      {...props}
    />
  );
}

export { Avatar, AvatarImage, AvatarFallback };
export type { LoyaltyTier };
